import React, { useEffect, useState } from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Config from "../../../Config";
import { get_paymentId } from "../../../../store/Actions/authActions";
import { orderTotals } from "../../../../utils/checkout";
import { dateKey } from "../../../../utils/slotSelection";
import { localeKey, productName } from "../../../../utils/catalogue";
import { formatMoney, formatOrderDate, formatSlot, isToday } from "../../../../utils/orders";
import {
  buildUpdatePayload,
  editErrorMessage,
  issueOpen,
  vatIncluded,
} from "../../../../utils/editOrder";
import { leaveOrderEdit } from "../../Orders/editOrder";
import { Icon, Sprite } from "../../Orders/icons";
import "./editRecap.css";

/**
 * Récapitulatif en mode édition — design « Modifier la commande.dc.html ».
 * Servi à /checkout tant qu'une commande est en cours de modification (voir
 * CheckoutEntry et Orders/editOrder).
 *
 * Le bandeau vert « Vous modifiez une commande » de l'ancienne page est
 * gardé en tête, à la demande : il porte la suppression et l'abandon, qui
 * passent maintenant par les dialogues du design.
 *
 * Verrous (editRestrictions) : lignes après le passage en blanchisserie,
 * collecte et suppression après la collecte, paiement une fois payé.
 * L'envoi est un PUT /customer/orders/:id (utils/editOrder).
 */

const capitalise = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

export function EditRecap({
  history,
  dispatch,
  context,
  cartItems,
  subtotal,
  address,
  isHome,
  pickup,
  pickupSlot,
  delivery,
  deliverySlot,
  cards,
  authUser,
}) {
  const { t, i18n } = useTranslation("app");
  const { t: tLegacy } = useTranslation();
  const lang = i18n.language === "fn" ? "fr" : i18n.language === "du" ? "nl" : "en";

  const headers = {
    uid: authUser?.user_uid,
    client: authUser?.user_client,
    "access-token": authUser?.user_access_token,
  };

  const [balance, setBalance] = useState(null);
  const [payment, setPayment] = useState(
    context?.paymentType === "Card" && context?.paymentMethodId
      ? { type: "Card", cardId: context.paymentMethodId }
      : context?.paymentType === "Wallet"
      ? { type: "Wallet" }
      : null
  );
  const [notesOpen, setNotesOpen] = useState(false);
  const [description, setDescription] = useState(undefined);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    Axios({ url: `${Config.apiURL}/customer/wallet`, method: "GET", headers })
      .then((response) => setBalance(Number(response?.data?.wallet?.amount) || 0))
      .catch(() => setBalance(null));
    dispatch(get_paymentId());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!context) {
    return <Redirect to="/orders" />;
  }

  const { restrictions } = context;
  const paid = context.paymentStatus === "paid";
  const lines = cartItems || [];
  const savedCards = (cards || []).filter((card) => card?.id);

  const pickupKey = dateKey(pickup?.date?.date);
  const deliveryKey = dateKey(delivery?.date?.date);
  const slots = { pickupKey, pickupSlot, deliveryKey, deliverySlot };
  const issueIsOpen = issueOpen(context, slots);
  const deliveryHighlight = issueIsOpen && context.issue === "delivery";

  const count = lines.reduce((sum, line) => sum + (line.quantity || 0), 0);
  const totals = orderTotals({ subtotal, promo: null, address, isHome });

  const slotLine = (key, slot) =>
    key && slot
      ? `${isToday(key) ? t("checkout.today") : formatOrderDate(key, lang)} · ${formatSlot(slot)}`
      : "—";

  const cardLabel = (card) =>
    card ? `${card.brand ? `${capitalise(card.brand)} ` : ""}•• ${card.last4}` : t("orders.paymentCard");
  const paidWith =
    context.paymentType === "Card"
      ? cardLabel(savedCards.find((card) => String(card.id) === String(context.paymentMethodId)))
      : t("paymentMethod.woshWalletTitle");

  const blocked =
    busy ||
    (restrictions.canEditProducts && count === 0) ||
    !pickupKey ||
    !pickupSlot ||
    !deliveryKey ||
    !deliverySlot ||
    issueIsOpen ||
    (!paid && !payment);

  /* ── Actions ─────────────────────────────────────────────── */

  const leave = async () => {
    await dispatch(leaveOrderEdit());
    history.push("/orders");
  };

  const update = async () => {
    setError(null);
    setBusy(true);
    try {
      await Axios({
        url: `${Config.apiURL}/customer/orders/${context.orderId}`,
        method: "PUT",
        headers,
        data: buildUpdatePayload({
          context,
          cartItems: lines,
          pickup,
          pickupSlot,
          delivery,
          deliverySlot,
          address,
          isHome,
          description,
          payment,
        }),
      });
    } catch (err) {
      setError(editErrorMessage(err?.response?.data, t));
      setBusy(false);
      return;
    }
    await leave();
  };

  const remove = async () => {
    setDialog(null);
    setError(null);
    setBusy(true);
    try {
      await Axios({ url: `${Config.apiURL}/customer/orders/${context.orderId}`, method: "DELETE", headers });
    } catch (err) {
      setError(editErrorMessage(err?.response?.data, t));
      setBusy(false);
      return;
    }
    await leave();
  };

  /* ── Rendu ───────────────────────────────────────────────── */

  const place = isHome ? t("checkout.homeLabel") : t("checkout.pointLabel");
  const pointName = address?.companies?.[0]?.company_name;
  const note = String(address?.comment || "").trim();

  return (
    <div className="wosh-ed">
      <Sprite />

      <div className="wosh-ed__banner">
        <div className="wosh-ed__banner-side">
          {restrictions.canDelete && (
            <button type="button" className="wosh-ed__banner-delete" onClick={() => setDialog("delete")}>
              {t("checkout.deleteOrder")}
            </button>
          )}
        </div>
        <p className="wosh-ed__banner-text">{tLegacy("You are currently editing an order")}</p>
        <div className="wosh-ed__banner-side wosh-ed__banner-side--end">
          <button
            type="button"
            className="wosh-ed__banner-close"
            aria-label={t("checkout.discard")}
            onClick={() => setDialog("discard")}
          >
            <Icon name="close" />
          </button>
        </div>
      </div>

      <div className="wosh-ed__page">
        <div className="wosh-ed__head">
          <button
            type="button"
            className="wosh-ed__back"
            aria-label={t("checkout.discard")}
            onClick={() => setDialog("discard")}
          >
            <Icon name="arrow-back" />
          </button>
          <h1 className="wosh-ed__title">{t("checkout.editOrderTitle")}</h1>
        </div>

        {issueIsOpen && (
          <div className="wosh-ed__issue" role="status">
            <Icon name="warning-outline" />
            {t(context.issue === "pickup" ? "checkout.issuePickupBanner" : "checkout.issueDeliveryBanner")}
          </div>
        )}

        {/* Votre commande */}
        <section className="wosh-ed__card">
          <div className="wosh-ed__card-head">
            <div className="wosh-ed__card-titles">
              <span className="wosh-ed__card-title">{t("checkout.yourOrder")}</span>
              <span className="wosh-ed__card-sub">
                {count} {count === 1 ? t("checkout.item") : t("checkout.items")}
              </span>
            </div>
            {restrictions.canEditProducts ? (
              <button type="button" className="wosh-ed__pill" onClick={() => history.push("/home")}>
                <Icon name="pencil" />
                {t("checkout.editItems")}
              </button>
            ) : (
              <span className="wosh-ed__locked">
                <Icon name="lock-closed" />
                {t("checkout.itemsLocked")}
              </span>
            )}
          </div>

          {lines.map((line) => {
            const name = productName(line, localeKey());
            const unit = (Number(line.price) || 0) / (line.quantity || 1);
            return (
              <div className="wosh-ed__line" key={line.id}>
                <span className="wosh-ed__tile">
                  {line.icon ? (
                    <img src={line.icon} alt="" />
                  ) : (
                    String(name || "?").charAt(0).toUpperCase()
                  )}
                </span>
                <span className="wosh-ed__line-body">
                  <span className="wosh-ed__line-name">{name}</span>
                  <span className="wosh-ed__line-unit">
                    {line.quantity} × {formatMoney(unit)}
                  </span>
                </span>
                <span className="wosh-ed__line-sum">{formatMoney(line.price)}</span>
              </div>
            );
          })}

          <div className="wosh-ed__rule" />
          <div className="wosh-ed__row">
            <span>
              {t("checkout.subtotal")}{" "}
              <span className="wosh-ed__vat">
                · {t("checkout.taxIncluded", { amount: formatMoney(vatIncluded(totals.total)) })}
              </span>
            </span>
            <strong>{formatMoney(totals.subtotal)}</strong>
          </div>
          {totals.fees.length === 0 ? (
            <div className="wosh-ed__row">
              <span>{t("checkout.deliveryFee")}</span>
              <strong className="wosh-ed__free">{t("checkout.free")}</strong>
            </div>
          ) : (
            totals.fees.map((fee) => (
              <div className="wosh-ed__row" key={fee.kind}>
                <span>{t(fee.kind === "service" ? "checkout.serviceFee" : "checkout.deliveryFee")}</span>
                <strong>{formatMoney(fee.amount)}</strong>
              </div>
            ))
          )}
          <div className="wosh-ed__total-row">
            <span>{t("checkout.total")}</span>
            <span>{formatMoney(totals.total)}</span>
          </div>
        </section>

        {/* Adresse */}
        <section className="wosh-ed__card">
          <span className="wosh-ed__card-title">
            {isHome ? t("checkout.deliveryTo") : t("checkout.collectionPoint")}
          </span>
          <div className="wosh-ed__place">
            <span className="wosh-ed__tile">
              <Icon name={isHome ? "home-outline" : "business-outline"} />
            </span>
            <span className="wosh-ed__place-body">
              <span className="wosh-ed__place-label">{!isHome && pointName ? pointName : place}</span>
              <span className="wosh-ed__place-address">{address?.address}</span>
              {note && <span className="wosh-ed__place-note">{note}</span>}
            </span>
          </div>
          {notesOpen ? (
            <textarea
              className="wosh-ed__notes"
              rows={2}
              maxLength={200}
              autoFocus
              value={description || ""}
              placeholder={t("checkout.forThisOrderPlaceholder")}
              aria-label={t("checkout.instrTitle")}
              onChange={(event) => setDescription(event.target.value)}
            />
          ) : (
            <button type="button" className="wosh-ed__link" onClick={() => setNotesOpen(true)}>
              {description ? t("checkout.editDeliveryInstr") : t("checkout.addDeliveryInstr")}
            </button>
          )}
        </section>

        {/* Planification */}
        <section className="wosh-ed__card wosh-ed__card--tight">
          <span className="wosh-ed__card-title">{t("checkout.schedule")}</span>

          {restrictions.canEditPickup ? (
            <div className="wosh-ed__slot">
              <span className="wosh-ed__tile">
                <Icon name="calendar-outline" />
              </span>
              <span className="wosh-ed__slot-body">
                <span className="wosh-ed__slot-label">{t("orderDetail.pickupLabel")}</span>
                <span className="wosh-ed__slot-value">{slotLine(pickupKey, pickupSlot)}</span>
              </span>
              <button type="button" className="wosh-ed__link" onClick={() => history.push("/checkout/pickup")}>
                {t("checkout.edit")}
              </button>
            </div>
          ) : (
            <div className="wosh-ed__slot wosh-ed__slot--locked">
              <span className="wosh-ed__tile wosh-ed__tile--muted">
                <Icon name="lock-closed" />
              </span>
              <span className="wosh-ed__slot-body">
                <span className="wosh-ed__slot-label">{t("orderDetail.pickupLabel")}</span>
                <span className="wosh-ed__slot-value">{slotLine(pickupKey, pickupSlot)}</span>
              </span>
              <span className="wosh-ed__locked">
                <Icon name="lock-closed" />
                {t("checkout.itemsLocked")}
              </span>
            </div>
          )}

          <div className="wosh-ed__rule" />

          {deliveryHighlight ? (
            <div className="wosh-ed__slot wosh-ed__slot--alert">
              <span className="wosh-ed__tile">
                <Icon name="time-outline" />
              </span>
              <span className="wosh-ed__slot-body">
                <span className="wosh-ed__slot-alert">{t("checkout.issueDeliverySlotHighlight")}</span>
                <span className="wosh-ed__slot-old">{slotLine(deliveryKey, deliverySlot)}</span>
              </span>
              <button type="button" className="wosh-ed__link" onClick={() => history.push("/checkout/delivery")}>
                {t("checkout.edit")}
              </button>
            </div>
          ) : (
            <div className="wosh-ed__slot">
              <span className="wosh-ed__tile">
                <Icon name="time-outline" />
              </span>
              <span className="wosh-ed__slot-body">
                <span className="wosh-ed__slot-label">{t("orderDetail.deliveryLabel")}</span>
                <span className="wosh-ed__slot-value">{slotLine(deliveryKey, deliverySlot)}</span>
              </span>
              <button type="button" className="wosh-ed__link" onClick={() => history.push("/checkout/delivery")}>
                {t("checkout.edit")}
              </button>
            </div>
          )}
        </section>

        {/* Paiement */}
        <section className="wosh-ed__card wosh-ed__card--tight">
          <span className="wosh-ed__card-title">{t("checkout.payment")}</span>

          {paid ? (
            <div className="wosh-ed__paid">
              <span className="wosh-ed__tile wosh-ed__tile--white">
                <Icon name="shield-checkmark-outline" />
              </span>
              <span className="wosh-ed__slot-body">
                <span className="wosh-ed__paid-title">{t("checkout.paidConfirmed")}</span>
                <span className="wosh-ed__card-sub">{paidWith}</span>
              </span>
              <span className="wosh-ed__paid-badge">{t("checkout.paid")}</span>
            </div>
          ) : (
            <div className="wosh-ed__pays" role="radiogroup" aria-label={t("checkout.payment")}>
              <PayOption
                on={payment?.type === "Wallet"}
                icon="wallet-outline"
                title={t("paymentMethod.woshWalletTitle")}
                sub={balance === null ? "" : t("paymentMethod.available", { balance: formatMoney(balance) })}
                onSelect={() => setPayment({ type: "Wallet" })}
              />
              {savedCards.map((card) => (
                <PayOption
                  key={card.id}
                  on={payment?.type === "Card" && String(payment.cardId) === String(card.id)}
                  icon="card-outline"
                  title={cardLabel(card)}
                  sub={
                    card.exp_month && card.exp_year
                      ? `${String(card.exp_month).padStart(2, "0")}/${String(card.exp_year).slice(-2)}`
                      : ""
                  }
                  onSelect={() => setPayment({ type: "Card", cardId: card.id })}
                />
              ))}
            </div>
          )}
        </section>

        {error && (
          <div className="wosh-ed__error" role="alert">
            <Icon name="alert-circle" />
            {error}
          </div>
        )}
      </div>

      <div className="wosh-ed__foot">
        <div className="wosh-ed__foot-inner">
          <div className="wosh-ed__foot-total">
            <span>{t("checkout.total")}</span>
            <strong>{formatMoney(totals.total)}</strong>
          </div>
          <button type="button" className="wosh-btn wosh-ed__cta" disabled={blocked} onClick={update}>
            {busy ? "…" : t("checkout.updateOrder")}
          </button>
        </div>
      </div>

      {dialog === "discard" && (
        <ConfirmDialog
          icon="arrow-back"
          title={t("checkout.discardTitle")}
          message={t("checkout.discardMsg")}
          cancel={t("checkout.keepEditing")}
          confirm={t("checkout.discard")}
          onCancel={() => setDialog(null)}
          onConfirm={() => {
            setDialog(null);
            leave();
          }}
        />
      )}
      {dialog === "delete" && (
        <ConfirmDialog
          icon="trash-outline"
          title={t("checkout.deleteOrderTitle")}
          message={t("checkout.deleteOrderMsg")}
          cancel={t("checkout.deleteOrderCancel")}
          confirm={t("checkout.deleteOrderConfirm")}
          onCancel={() => setDialog(null)}
          onConfirm={remove}
        />
      )}
    </div>
  );
}

function PayOption({ on, icon, title, sub, onSelect }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      className={`wosh-ed__pay${on ? " wosh-ed__pay--on" : ""}`}
      onClick={onSelect}
    >
      <span className={`wosh-ed__pay-icon${on ? " wosh-ed__pay-icon--on" : ""}`}>
        <Icon name={icon} />
      </span>
      <span className="wosh-ed__slot-body">
        <span className="wosh-ed__pay-title">{title}</span>
        {sub && <span className="wosh-ed__card-sub">{sub}</span>}
      </span>
      <span className={`wosh-ed__radio${on ? " wosh-ed__radio--on" : ""}`}>{on && <Icon name="checkmark-sharp" />}</span>
    </button>
  );
}

/** Dialogue de confirmation du design (ConfirmDialog.dc.html), ton « danger ». */
function ConfirmDialog({ icon, title, message, cancel, confirm, onCancel, onConfirm }) {
  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onCancel();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="wosh-ed-dialog" onClick={onCancel}>
      <div
        className="wosh-ed-dialog__box"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="wosh-ed-dialog-title"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="wosh-ed-dialog__icon">
          <Icon name={icon} />
        </span>
        <h2 className="wosh-ed-dialog__title" id="wosh-ed-dialog-title">
          {title}
        </h2>
        <p className="wosh-ed-dialog__message">{message}</p>
        <div className="wosh-ed-dialog__actions">
          <button type="button" className="wosh-ed-dialog__cancel" onClick={onCancel} autoFocus>
            {cancel}
          </button>
          <button type="button" className="wosh-ed-dialog__confirm" onClick={onConfirm}>
            {confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  context: state?.auth?.editContext,
  cartItems: state?.cart?.cartItems,
  subtotal: state?.cart?.totalAmount,
  address: state?.auth?.selectedAddress?.[0],
  isHome: state?.cart?.home === true,
  pickup: state?.cart?.pickup,
  pickupSlot: state?.auth?.pickupTime,
  delivery: state?.cart?.time,
  deliverySlot: state?.auth?.deliveryTime,
  cards: state?.auth?.paymentId,
  authUser: state?.auth?.user,
});

export default connect(mapStateToProps)(EditRecap);
